<template>
  <div class="selector-search-header">
    <q-input
      v-if="theModel !== undefined"
      outlined
      dense
      ref="header_search_input"
      color="white"
      class="selector-search-header__input"
      v-model="theModel"
      hide-bottom-space
      :placeholder="showFreeInput ? $t('search_or_new') : $t('search_label')"
      debounce="300"
      :clearable="!useOwnClearButton"
      :clear-icon="clearIconName"
      @clear="theModel = ''"
      @keydown.down.exact.prevent="emitArrowDown"
      @keydown.up.exact.prevent="emitArrowUp"
      @keyup.enter.stop.prevent="emitEnter"
    >
      <template v-slot:append>
        <button
          v-if="useOwnClearButton && theModel"
          type="button"
          class="selector-search-header__icon-btn"
          :aria-label="$t('clear_field')"
          @mousedown.stop.prevent
          @click.stop="theModel = ''"
        >
          <span
            class="selector-search-header__icon selector-search-header__icon--clear"
          ></span>
          <q-tooltip
            :delay="300"
            anchor="top middle"
            self="center middle"
            class="aux-sel-tooltip"
          >
            {{ $t("clear_field") }}
          </q-tooltip>
        </button>
        <button
          v-if="showFreeInput && theModel !== undefined && freeInputTooltip"
          type="button"
          class="selector-search-header__icon-btn"
          :aria-label="freeInputTooltip"
          @mousedown.stop.prevent
          @click.stop="emitFreeInput"
        >
          <svg
            class="selector-search-header__icon-svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 3.33333V12.6667"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M3.33333 8H12.6667"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          <q-tooltip
            :delay="300"
            anchor="top middle"
            self="center middle"
            class="aux-sel-tooltip"
          >
            {{ freeInputTooltip }}
          </q-tooltip>
        </button>
        <q-btn
          v-else-if="showFreeInput && theModel !== undefined"
          @click.stop.prevent="emitFreeInput"
          dense
          flat
          color="accent"
          size="md"
          icon="add"
          class="q-pa-none"
        />
        <svg
          v-if="!hideSearchIcon"
          class="selector-search-header__search-icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.9998 13.9998L11.1064 11.1064"
            stroke="#999999"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
            stroke="#999999"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>
    </q-input>
    <button
      v-if="showCloseButton"
      class="selector-search-header__close"
      :aria-label="$t('close_selector')"
      @click="emitClose"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15 5L5 15"
          stroke="#979797"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M5 5L15 15"
          stroke="#979797"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from "vue-facing-decorator";
import { i18n } from "../../../i18n";

@Component({
  name: "SelectorSearchHeader",
  expose: ["focusField"],
})
export default class SelectorSearchHeader extends Vue {
  @Prop({ default: null })
  modelValue!: string;
  @Prop({ default: i18n.global.t("new_value") + ": " })
  newValueLabel?: string;
  @Emit("update:modelValue") emitModelValue(arg: string) {
    return arg;
  }

  @Prop({ default: false })
  showFreeInput?: boolean;

  @Prop({ default: true })
  showCloseButton?: boolean;

  @Prop({ default: false })
  hideSearchIcon?: boolean;

  // Своя кнопка очистки нужна там, где у иконок должны быть подсказки и зелёный hover:
  // штатную иконку Quasar ни подписать, ни перекрасить нельзя.
  @Prop({ default: false })
  useOwnClearButton?: boolean;

  @Prop({ default: "" })
  freeInputTooltip?: string;
  @Emit("keyUp") emitArrowUp() {
    return;
  }
  @Emit("keyDown") emitArrowDown() {
    return;
  }
  @Emit("keyEnter") emitEnter() {
    return;
  }
  @Emit("inputPressed") emitFreeInput() {
    return;
  }
  @Emit("close") emitClose() {
    return;
  }

  private clearIconUrl: string = require("@/assets/arrow_back.svg");

  get clearIconName(): string {
    return `img:${this.clearIconUrl}`;
  }

  get theModel(): string {
    return this.modelValue;
  }

  set theModel(val: string) {
    this.emitModelValue(val);
  }

  focusField() {
    const that = this;
    setTimeout(() => {
      if (that.$refs.header_search_input) {
        // @ts-ignore
        that.$refs.header_search_input.focus();
      }
    }, 100);
  }
}
</script>

<style lang="scss" scoped>
.selector-search-header {
  position: relative;
  padding: 12px 38px 8px 12px;

  :deep(.q-field) {
    padding-bottom: 0 !important;
  }

  &__input {
    width: 100%;
    font-size: small !important;

    :deep(.q-field__control) {
      height: 32px;
      min-height: 32px;
      border-radius: 8px;
      border: 1px solid var(--color-border, #d2d2d2);
      background: var(--Background-White, #fff);
    }

    :deep(.q-field__control::before),
    :deep(.q-field__control::after) {
      border: none !important;
    }

    &.q-field--focused :deep(.q-field__control) {
      border-color: var(--color-accent, #3f884f);
    }

    :deep(.q-field__append) {
      height: 32px;
      padding: 0;
      align-items: center;
    }

    :deep(.q-field__inner),
    :deep(.q-field__control-container) {
      height: 32px;
    }

    :deep(.q-field__control) {
      padding: 0 12px;
    }

    :deep(.q-field__marginal) {
      height: 32px;
    }

    :deep(.q-field__native::placeholder) {
      color: #999;
    }
  }

  &__search-icon {
    display: block;
    flex-shrink: 0;
    position: relative;
    top: -1px;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: #979797;
  }

  &__icon-btn:hover {
    color: var(--color-accent, #3f884f);
  }

  &__icon-svg {
    display: block;
  }

  &__icon {
    display: block;
    width: 16px;
    height: 16px;
    background-color: #979797;
  }

  &__icon--clear {
    mask: url("@/assets/arrow_back.svg") no-repeat center / contain;
    -webkit-mask: url("@/assets/arrow_back.svg") no-repeat center / contain;
  }

  &__icon-btn:hover .selector-search-header__icon {
    background-color: var(--color-accent, #3f884f);
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>
